import { useState, useEffect } from 'react';
import Modal from '../Modal';

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' }
];

const COLOR_OPTIONS = [
  '#C45D3A', // Terracotta (default)
  '#5B6BD4', // Blue
  '#2D8A5F', // Green
  '#8B5CF6', // Purple
  '#E67E22', // Orange
  '#6B7280'  // Gray
];

function generateTimeOptions() {
  const options = [];
  for (let hour = 6; hour <= 21; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const h = String(hour).padStart(2, '0');
      const m = String(min).padStart(2, '0');
      const time = `${h}:${m}`;
      const label = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      options.push({ value: time, label });
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Generate agenda items from template (with new linked fields)
function generateAgendaFromTemplate(template) {
  if (!template?.defaultAgendaItems) return [];
  return template.defaultAgendaItems.map(item => ({
    ...item,
    id: generateUUID(),
    isCompleted: false,
    notes: '',
    linkedDocumentIds: [],
    linkedPolicyIds: []
  }));
}

export default function MeetingModal({
  isOpen,
  onClose,
  onSave,
  editItem,
  templates = [],
  series = [],
  defaultDate
}) {
  const [formData, setFormData] = useState({
    title: '',
    date: defaultDate || '',
    startTime: '09:00',
    durationMinutes: 60,
    templateId: '',
    seriesId: '',
    agendaItems: [],
    color: '#C45D3A'
  });
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens/closes or edit item changes
  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        setFormData({
          title: editItem.title || '',
          date: editItem.date || defaultDate || '',
          startTime: editItem.startTime || '09:00',
          durationMinutes: editItem.durationMinutes || 60,
          templateId: editItem.templateId || '',
          seriesId: editItem.seriesId || '',
          agendaItems: editItem.agendaItems || [],
          color: editItem.color || '#C45D3A'
        });
      } else {
        setFormData({
          title: '',
          date: defaultDate || '',
          startTime: '09:00',
          durationMinutes: 60,
          templateId: '',
          seriesId: '',
          agendaItems: [],
          color: '#C45D3A'
        });
      }
    }
  }, [isOpen, editItem, defaultDate]);

  // Handle template selection - populates agenda items from template
  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        templateId,
        title: prev.title || template.name,
        durationMinutes: template.defaultDurationMinutes || prev.durationMinutes,
        color: template.color || prev.color,
        agendaItems: generateAgendaFromTemplate(template)
      }));
    } else {
      setFormData(prev => ({ ...prev, templateId: '', agendaItems: [] }));
    }
  };

  // Calculate end time
  const calculateEndTime = () => {
    const [hours, minutes] = formData.startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + formData.durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    setSaving(true);
    try {
      await onSave({
        ...formData,
        endTime: calculateEndTime()
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editItem ? 'Edit Meeting' : 'New Meeting'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5">
            Meeting Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g., Weekly Leadership Sync"
            className="input-premium w-full"
            autoFocus
          />
        </div>

        {/* Template selector */}
        {templates.length > 0 && !editItem && (
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Use Template (optional)
            </label>
            <select
              value={formData.templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="input-premium w-full"
            >
              <option value="">No template</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Date and Time row */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              className="input-premium w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Start Time
            </label>
            <select
              value={formData.startTime}
              onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
              className="input-premium w-full"
            >
              {TIME_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Duration
            </label>
            <select
              value={formData.durationMinutes}
              onChange={(e) => setFormData(prev => ({ ...prev, durationMinutes: Number(e.target.value) }))}
              className="input-premium w-full"
            >
              {DURATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5">
            Color
          </label>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map(color => (
              <button
                key={color}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color }))}
                className={`w-6 h-6 rounded-full transition-all ${
                  formData.color === color ? 'ring-2 ring-offset-2 ring-accent' : ''
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        {/* Template Info */}
        {formData.templateId && (
          <div className="text-xs text-text-tertiary bg-surface-hover px-3 py-2 rounded-lg">
            Template agenda will be applied. You can customize it after creating the meeting.
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-text-secondary bg-surface-hover hover:bg-surface-active rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!formData.title.trim() || saving}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-accent hover:bg-accent-bright rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
